import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/lib/supabaseClient';
import Swal from 'sweetalert2';

interface Module { id: string; name: string; category: string; is_core: boolean; }
interface PlanModuleRow { plan_id: string; module_id: string; is_included: boolean; limit_value: number | null; }

const SW = { background: '#ffffff', color: '#0f172a' };

const PLANS = [
  { id: 'free',       name: 'Free',       price: '$0',  color: 'text-slate-500',   badgeBg: 'bg-slate-100',      border: 'border-slate-200' },
  { id: 'pro',        name: 'Pro',        price: '$29', color: 'text-amber-700',   badgeBg: 'bg-amber-50',       border: 'border-amber-200' },
  { id: 'enterprise', name: 'Enterprise', price: '$79', color: 'text-emerald-700', badgeBg: 'bg-emerald-50',     border: 'border-emerald-200' },
];

const CATEGORIES = ['Production', 'Website', 'Automation', 'Settings'];

const CAT_STYLE: Record<string, { dot: string; label: string }> = {
  Production: { dot: 'bg-emerald-400', label: 'text-emerald-600' },
  Website:    { dot: 'bg-cyan-400',    label: 'text-cyan-600' },
  Automation: { dot: 'bg-amber-400',   label: 'text-amber-600' },
  Settings:   { dot: 'bg-rose-400',    label: 'text-rose-600' },
};

const MODULE_ICONS: Record<string, string> = {
  clients: 'ri-user-3-line', contracts: 'ri-file-text-line', payments: 'ri-bank-card-line',
  jobs: 'ri-briefcase-line', public_website: 'ri-global-line', client_portal: 'ri-user-shared-line',
  basic_branding: 'ri-palette-line', digital_invitations: 'ri-mail-send-line', rsvp: 'ri-calendar-check-line',
  media_packages: 'ri-folder-image-line', dvd_online: 'ri-disc-line', photo_selection: 'ri-image-line',
  final_deliveries: 'ri-send-plane-line', ai_sales_assistant: 'ri-robot-line', appointments: 'ri-calendar-line',
  whatsapp_notifications: 'ri-whatsapp-line', email_automations: 'ri-mail-settings-line',
  custom_domain: 'ri-earth-line', extra_storage: 'ri-hard-drive-line',
};

const mKey = (planId: string, moduleId: string) => `${planId}__${moduleId}`;

export default function PlanModulesSection() {
  const [modules, setModules]   = useState<Module[]>([]);
  const [matrix, setMatrix]     = useState<Record<string, boolean>>({});
  const [limits, setLimits]     = useState<Record<string, number | null>>({});
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [loading, setLoading]   = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const [modRes, pmRes] = await Promise.all([
      supabase.from('saas_modules').select('id, name, category, is_core').order('category').order('name'),
      supabase.from('saas_plan_modules').select('plan_id, module_id, is_included, limit_value'),
    ]);
    const mods = (modRes.data ?? []) as Module[];
    const rows = (pmRes.data ?? []) as PlanModuleRow[];
    const mat: Record<string, boolean> = {};
    const lim: Record<string, number | null> = {};
    PLANS.forEach((p) => mods.forEach((m) => { mat[mKey(p.id, m.id)] = false; }));
    rows.forEach((r) => { mat[mKey(r.plan_id, r.module_id)] = r.is_included; lim[mKey(r.plan_id, r.module_id)] = r.limit_value; });
    setModules(mods); setMatrix(mat); setLimits(lim); setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (planId: string, moduleId: string) => {
    if (!editMode) return;
    const key = mKey(planId, moduleId);
    setMatrix((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const save = async () => {
    setSaving(true);
    const upserts = PLANS.flatMap((p) => modules.map((m) => ({
      plan_id: p.id, module_id: m.id, is_included: matrix[mKey(p.id, m.id)] ?? false, limit_value: limits[mKey(p.id, m.id)] ?? null,
    })));
    const { error } = await supabase.from('saas_plan_modules').upsert(upserts, { onConflict: 'plan_id,module_id' });
    setSaving(false);
    if (error) { Swal.fire({ icon: 'error', title: 'Error', text: error.message, ...SW }); return; }
    setEditMode(false);
    Swal.fire({ icon: 'success', title: 'Cambios guardados', timer: 1400, showConfirmButton: false, ...SW });
  };

  const cancel = () => { setEditMode(false); load(); };
  const modulesByCategory = (cat: string) => modules.filter((m) => m.category === cat);
  const planInclCount = (planId: string) => modules.filter((m) => matrix[mKey(planId, m.id)]).length;

  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-slate-500 text-xs uppercase tracking-wider font-medium">Plan Modules</h3>
          <p className="text-slate-400 text-xs mt-0.5">Qué módulos incluye cada plan</p>
        </div>
        <div className="flex items-center gap-2">
          {editMode ? (
            <>
              <button onClick={cancel} className="px-3 py-1.5 text-xs text-slate-500 hover:text-slate-900 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap">Cancelar</button>
              <button onClick={save} disabled={saving} className="px-4 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50">
                {saving ? 'Guardando...' : 'Guardar cambios'}
              </button>
            </>
          ) : (
            <button onClick={() => setEditMode(true)} className="flex items-center gap-2 px-3 py-1.5 text-xs text-slate-500 hover:text-slate-900 border border-slate-200 hover:border-slate-300 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap">
              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-edit-line"></i></span>
              Editar
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="bg-white border border-slate-200 rounded-xl py-14 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="grid grid-cols-[1fr_120px_120px_120px] border-b border-slate-200 bg-slate-50">
            <div className="px-5 py-4"><p className="text-slate-500 text-xs font-medium">Módulo</p></div>
            {PLANS.map((p) => (
              <div key={p.id} className="px-4 py-4 text-center border-l border-slate-200">
                <p className={`text-sm font-bold ${p.color}`}>{p.name}</p>
                <p className="text-slate-400 text-xs">{p.price}/mes</p>
                <p className="text-slate-300 text-[10px] mt-1">{planInclCount(p.id)} módulos</p>
              </div>
            ))}
          </div>

          {CATEGORIES.map((cat) => {
            const mods = modulesByCategory(cat);
            if (mods.length === 0) return null;
            const cs = CAT_STYLE[cat] ?? { dot: 'bg-slate-300', label: 'text-slate-500' };
            return (
              <div key={cat}>
                <div className="grid grid-cols-[1fr_120px_120px_120px] border-b border-slate-100 bg-slate-50/50">
                  <div className="px-5 py-2.5 flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${cs.dot}`}></span>
                    <span className={`text-[11px] font-semibold uppercase tracking-wider ${cs.label}`}>{cat}</span>
                  </div>
                  {PLANS.map((p) => <div key={p.id} className="border-l border-slate-100" />)}
                </div>
                {mods.map((mod, idx) => {
                  const isLast = idx === mods.length - 1;
                  const icon = MODULE_ICONS[mod.id] ?? 'ri-apps-line';
                  return (
                    <div key={mod.id} className={`grid grid-cols-[1fr_120px_120px_120px] ${!isLast ? 'border-b border-slate-100' : ''} hover:bg-slate-50/50 transition-colors`}>
                      <div className="px-5 py-3 flex items-center gap-3">
                        <span className={`w-7 h-7 flex items-center justify-center rounded-md bg-slate-100 shrink-0`}>
                          <i className={`${icon} text-xs ${cs.label}`}></i>
                        </span>
                        <div>
                          <p className="text-slate-700 text-sm">{mod.name}</p>
                          {mod.is_core && <span className="text-[10px] text-emerald-600 font-medium">Core</span>}
                        </div>
                      </div>
                      {PLANS.map((plan) => {
                        const key = mKey(plan.id, mod.id);
                        const included = matrix[key] ?? false;
                        const lv = limits[key];
                        return (
                          <div key={plan.id}
                            className={`border-l border-slate-100 flex flex-col items-center justify-center py-3 ${editMode ? 'cursor-pointer' : ''}`}
                            onClick={() => toggle(plan.id, mod.id)}>
                            {included ? (
                              <span className={`w-6 h-6 flex items-center justify-center rounded-full transition-all ${editMode ? `${plan.badgeBg} ${plan.color} hover:opacity-70` : `${plan.badgeBg} ${plan.color}`}`}>
                                <i className="ri-check-line text-xs"></i>
                              </span>
                            ) : (
                              <span className={`w-6 h-6 flex items-center justify-center rounded-full transition-all ${editMode ? 'bg-slate-100 text-slate-300 hover:bg-slate-200' : 'text-slate-200'}`}>
                                <i className="ri-close-line text-xs"></i>
                              </span>
                            )}
                            {lv !== null && lv !== undefined && included && (
                              <span className="text-[10px] text-slate-400 mt-0.5">lím. {lv}</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            );
          })}

          <div className="px-5 py-3 border-t border-slate-100 flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <span className="w-4 h-4 flex items-center justify-center rounded-full bg-blue-50 text-blue-600"><i className="ri-check-line text-[10px]"></i></span>
              <span className="text-slate-400 text-xs">Incluido</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-4 h-4 flex items-center justify-center rounded-full bg-slate-100 text-slate-300"><i className="ri-close-line text-[10px]"></i></span>
              <span className="text-slate-400 text-xs">No incluido</span>
            </div>
            {editMode && <span className="text-amber-600 text-xs ml-auto">Haz clic en las celdas para cambiar</span>}
          </div>
        </div>
      )}
    </section>
  );
}
